// MACHINERY V2 (Stroje) — profile hero, layout C (M2.4).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.4 + data package §1/§2.
// Reference impl: design_handoff_machinery_v3/machinery-profile-mash.jsx
// lines ~109-145 (hero markup) + artboards 0504 / 0505.
//
// COMPOSITION (artboard 0504, left to right):
//   photo 170x112 | eyebrow + name + brand line + identity rows + live ribbon |
//   190px action stack (primary SR · request · kebab)
// The hero then flows DIRECTLY into the pill tabs — the 4-tile KPI strip the
// artboard draws between them is CUT (§9 D3, owner ruling: no data source, no
// placeholder, no KpiStrip.tsx).
//
// >>> THE HERO OWNS IDENTITY. THERE IS NO LEFT COLUMN. <<<
// Manager ruling 2026-08-22: the designer layout wins on STRUCTURE. Layout C
// deliberately deleted the sidebar and rotated its content into this band
// (0505: "Hero proportions match the V2 sidebar's content, just rotated").
// Data-package §2 FIELD parity is still FULL, satisfied inside the hero — see
// ProfileIdentityRows.tsx, which binds every sidebar field.
//
// STATUS RENDERING IS NOT DECIDED HERE. The live ribbon paints exclusively from
// `presentation` (deriveCardPresentation, M2.1) — this component never tests
// `alarms.length`, `conn` or `status`, and takes `presentation.dotConn` for the
// connectivity dot rather than the raw `conn`. Identical discipline to
// FleetCard/FleetRow, and the same one-footer rule: alarm strip OR program line
// OR the no-telemetry note, never two.
//
// ACTION GATING IS INHERITED, NOT INVENTED (§10). The two hero buttons render
// only when `buildFleetActions` put their entry in the machine's action set, so
// a Worker, a decommissioned machine and a read-only user all collapse exactly
// as they do on the fleet. See useProfileActions.ts.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useGalleryImages } from "../../../../shared/components/gallery";
import { getMachineDisplayName } from "../../../../shared/utils/machineUtils";
import { Machine } from "../../models/models";
import FleetCardMenu, { FleetMenuAction } from "../fleet/FleetCardMenu";
import { MachinePhoto } from "../fleet/fleetCardAtoms";
import {
  MachineryV2CardPresentation,
  MachineryV2Telemetry,
} from "../fleet/cardStates";
import { HeroEyebrow, HeroNameRow } from "./profileHeroAtoms";
import ProfileIdentityRows from "./ProfileIdentityRows";
import ProfileLiveRibbon from "./ProfileLiveRibbon";

export interface ProfileHeroProps {
  machine: Machine;
  telemetry: MachineryV2Telemetry;
  presentation: MachineryV2CardPresentation;
  /** Kebab entries, already gated by buildFleetActions. */
  actions: FleetMenuAction[];
  /** Null when the action set does not contain the entry (gate or state). */
  onCreateServiceReport: (() => void) | null;
  onCreateServiceRequest: (() => void) | null;
}

const ProfileHero: React.FC<ProfileHeroProps> = ({
  machine,
  telemetry,
  presentation: p,
  actions,
  onCreateServiceReport,
  onCreateServiceRequest,
}) => {
  const { translate } = useTranslation("myMachinery");
  const decommissioned = machine.isDecommissioned === true;

  // The SHARED 4-tier fallback (§2): files[] -> image -> catalog images ->
  // catalog hero URL. Same call the fleet card makes; never re-implemented.
  const images = useGalleryImages({
    type: "machine",
    image: machine.image?.content || null,
    catalogImageUrl:
      machine.catalogModel?.imageStandardUrl || machine.catalogModel?.imageUrl || null,
    catalogImages: machine.catalogModel?.images || [],
    files: machine.files?.map((f) => ({ name: f.name, content: f.content })) || [],
  });

  // REVIEW (M2.4 phase 2). The 190px action column was UNCONDITIONAL while all
  // three of its children are conditional, so it could paint as a blank 190px
  // gutter (plus the section's 16px gap) beside the hero. It is reachable: a
  // Worker on a DECOMMISSIONED machine gets no hero buttons and an EMPTY kebab
  // list, because useProfileActions filters `profile` (self-referential here)
  // and `restore` (promoted to ProfileDecommissionBanner) out via
  // PROMOTED_OUT_OF_KEBAB, QR is Machinery:Write, and `reportProblem` is
  // suppressed on a decommissioned machine. Render the column only when it has
  // at least one child.
  const hasActionColumn =
    onCreateServiceReport !== null ||
    onCreateServiceRequest !== null ||
    actions.length > 0;

  return (
    <section
      style={{
        background: "var(--card)",
        border: decommissioned ? "1px dashed var(--border)" : "1px solid var(--border)",
        borderRadius: 12,
        boxShadow: "var(--shadow)",
        padding: 16,
        display: "flex",
        // OVERFLOW SEATBELT ONLY (M2.4 review, ruling B). This row is
        // 170 photo + content + a 190 non-shrinking action column; without the
        // wrap that plus the padding exceeds a narrow viewport and the card
        // overflowed horizontally. It is deliberately NOT a responsive
        // treatment — no breakpoint read, no 3-tier logic here.
        //
        // >>> THE PHONE/TABLET PASS IS DONE, AND IT IS NOT IN THIS FILE. <<<
        // This comment used to end "M2.11 owns the real phone/tablet pass for
        // this hero". M2.11 was superseded by the M2.16 mobile sweep and M2.16d
        // is that pass: below 1280 ProfileBody mounts ProfileHeroPhone.tsx
        // INSTEAD of this component, so this section now only ever renders at
        // >= 1280 and the seatbelt below is what it always was — a seatbelt,
        // for a viewport this layout no longer has to reach.
        flexWrap: "wrap",
        gap: 16,
        minWidth: 0,
        opacity: decommissioned ? 0.75 : 1,
      }}
    >
      <MachinePhoto
        src={images.length > 0 ? images[0].src : null}
        alt={getMachineDisplayName(machine)}
        grayscale={decommissioned}
        width={170}
        height={112}
      />

      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        {/* Same element, same inputs — the derivation moved to
            profileHeroAtoms at M2.16d so the below-1280 band could restore the
            eyebrow without describing the machine a second way. */}
        <HeroEyebrow machine={machine} />

        <HeroNameRow machine={machine} decommissioned={decommissioned} />

        <ProfileIdentityRows machine={machine} />

        <div style={{ flex: 1 }} />

        {/* Live state ribbon + the contract's single footer. */}
        <ProfileLiveRibbon
          telemetry={telemetry}
          presentation={p}
          decommissioned={decommissioned}
        />
      </div>

      {/* Action stack — artboard 0504 top-right. Guarded: see hasActionColumn. */}
      {hasActionColumn && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            width: 190,
            // The column never shrinks below its 190px buttons — it wraps to
            // the next line instead (see the flexWrap note on the section).
            minWidth: 190,
            flexShrink: 0,
          }}
        >
          {onCreateServiceReport && (
            <button
              type="button"
              className="btn btn-mod"
              style={{ justifyContent: "center" }}
              onClick={onCreateServiceReport}
            >
              {"＋ " + translate("myMachinery:v2.profile.newReport")}
            </button>
          )}
          {onCreateServiceRequest && (
            <button
              type="button"
              className="btn"
              style={{ justifyContent: "center" }}
              onClick={onCreateServiceRequest}
            >
              {"＋ " + translate("myMachinery:v2.profile.newRequest")}
            </button>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <FleetCardMenu
              actions={actions}
              ariaLabel={translate("myMachinery:v2.profile.actionsMenu")}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default ProfileHero;
